import { supabaseClient } from '../../services/supabase';
import { standardLegend, standardTitle } from '../../utils/chartFormatting';

const BACHELORS_LEVEL = "Bachelor\\'s degree or higher";

/**
 * Fetches educational attainment (bachelor's or higher) over time for a location and California,
 * aggregated across race so the chart shows overall % over time regardless of race.
 */
export default async function fetchEducationalAttainmentOverTimeSupabase(location_name) {
  try {
    const localQuery = supabaseClient
      .from('educational_attain_race')
      .select('year, race, gender, percent, count')
      .eq('location_name', location_name)
      .eq('educ_level', BACHELORS_LEVEL)
      .gte('year', 2010)
      .lte('year', 2023)
      .order('year', { ascending: true });

    const californiaQuery = supabaseClient
      .from('educational_attain_race')
      .select('year, race, gender, percent, count')
      .eq('location_name', 'California')
      .eq('educ_level', BACHELORS_LEVEL)
      .gte('year', 2010)
      .lte('year', 2023)
      .order('year', { ascending: true });

    const { data: localRows, error: localError } = await localQuery;
    const { data: californiaRows, error: californiaError } = await californiaQuery;

    if (localError) throw localError;
    if (californiaError) console.warn('Error fetching California education over time:', californiaError);

    // Aggregate by year: use gender=Total rows only, then weighted average of percent by count (percent is 0–1)
    const aggregateByYear = (rows) => {
      if (!rows || rows.length === 0) return [];
      const byYear = new Map();
      rows
        .filter((r) => r.gender === 'Total')
        .forEach((r) => {
          const y = Number(r.year);
          const pct = Number(r.percent) != null ? Number(r.percent) : 0;
          const cnt = Number(r.count) != null && !Number.isNaN(Number(r.count)) ? Number(r.count) : 0;
          if (!byYear.has(y)) byYear.set(y, { sumPctCount: 0, sumCount: 0 });
          const cell = byYear.get(y);
          cell.sumPctCount += pct * (cnt || 1);
          cell.sumCount += cnt || 1;
        });
      return Array.from(byYear.entries())
        .map(([year, { sumPctCount, sumCount }]) => ({
          year,
          percent: sumCount > 0 ? (sumPctCount / sumCount) * 100 : null
        }))
        .sort((a, b) => a.year - b.year);
    };

    const localByYear = aggregateByYear(localRows || []);
    const californiaByYear = aggregateByYear(californiaRows || []);

    const allYears = [
      ...new Set([
        ...localByYear.map((d) => d.year),
        ...californiaByYear.map((d) => d.year)
      ])
    ].sort((a, b) => a - b);

    const localPcts = allYears.map((y) => {
      const row = localByYear.find((d) => d.year === y);
      return row?.percent ?? null;
    });
    const californiaPcts = allYears.map((y) => {
      const row = californiaByYear.find((d) => d.year === y);
      return row?.percent ?? null;
    });

    const datasets = [];
    if (localByYear.length > 0) {
      datasets.push({
        label: location_name,
        data: localPcts,
        borderColor: '#2563eb',
        backgroundColor: 'rgba(37, 99, 235, 0.1)',
        borderWidth: 2,
        fill: true,
        tension: 0.2,
        pointRadius: 3,
        pointHoverRadius: 5
      });
    }
    if (californiaByYear.length > 0) {
      datasets.push({
        label: 'California',
        data: californiaPcts,
        borderColor: '#059669',
        backgroundColor: 'rgba(5, 150, 105, 0.1)',
        borderWidth: 2,
        fill: true,
        tension: 0.2,
        pointRadius: 3,
        pointHoverRadius: 5
      });
    }

    return {
      chartData: {
        labels: allYears.map(String),
        datasets
      },
      chartOptions: {
        type: 'line',
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: { top: 8, right: 8, bottom: 40, left: 8 }
        },
        plugins: {
          legend: {
            ...standardLegend,
            display: true,
            align: 'center',
            fullSize: true
          },
          title: standardTitle(
            `Educational Attainment Over Time - ${location_name} vs California (${Math.min(...allYears)}–${Math.max(...allYears)})`
          ),
          tooltip: {
            enabled: true,
            mode: 'index',
            intersect: false,
            callbacks: {
              label: (context) => {
                const v = context.parsed.y;
                return `${context.dataset.label}: ${v != null ? Number(v).toFixed(1) + '%' : 'N/A'}`;
              }
            }
          }
        },
        scales: {
          x: {
            title: { display: true, text: 'Year' }
          },
          y: {
            title: { display: true, text: "% with Bachelor's Degree or Higher" },
            min: 0,
            max: 100,
            ticks: { callback: (v) => (typeof v === 'number' ? `${v}%` : v) }
          }
        }
      }
    };
  } catch (err) {
    console.error('Failed to generate educational attainment over time chart:', err);
    throw err;
  }
}
